import { defineComponent as _defineComponent } from 'vue'
import {
  computed, PropType, ref, toRefs, watch
} from 'vue';
import type { Ref } from 'vue';
import BCXSortableTableHeader from '@/components/molecules/BCXSortableTableHeader.vue';
import BCXPaginatedContent from '@/components/molecules/BCXPaginatedContent.vue';
import useResponsiveness from '@/mixins/useResponsiveness';
import SearchInput from '@/components/molecules/SearchInput.vue';
import {
  BcxTableFilter, BcxTableProgressDisplay, BcxTableQuickFilter
} from '@/models/BcxTable';
import BCXMultiselectFilter from '@/components/molecules/BCXMultiselectFilter.vue';
import { reactiveComputed, watchImmediate } from '@vueuse/core';
import BCXFilterButtons from '@/components/BCXFilterButtons.vue';
import useBcxTableSearchFilterSort, {
  BcxTablePinnedItemPosition,
  BcxTablePriorityItems,
  BcxTableSearchFilterSortOptions
} from '@/mixins/useBcxTableSearchFilterSort';
import BCXPagination from '@/components/molecules/BCXPagination.vue';
import useI18n from '@/mixins/useI18n';
import BCXButton from '@/components/molecules/BCXButton.vue';
import { createStorageBackedRef } from '@/mixins/useBcxPersistence';
import type { BcxPersistenceMode } from '@/mixins/useBcxPersistence';


export default /*#__PURE__*/_defineComponent({
  __name: 'BCXTable',
  props: {
  fieldNames: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  headerFields: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  headerTitles: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  mobileHiddenFields: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  rows: {
    type: Array as PropType<unknown[]>,
    default: () => []
  },
  templateColumns: {
    type: String,
    default: '1fr'
  },
  mobileTemplateColumns: {
    type: String,
    default: '1fr'
  },
  mobileGridAreas: {
    type: String,
    default: ''
  },
  gap: {
    type: String,
    default: '0'
  },
  sortableFields: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  persistanceKey: {
    type: String,
    default: ''
  },
  persistenceMode: {
    type: String as PropType<BcxPersistenceMode>,
    default: 'localStorage'
  },
  defaultSortField: {
    type: String,
    default: ''
  },
  defaultSortAscending: Boolean,
  sortValueCallback: {
    type: Function,
    default: null
  },
  displayValueCallback: {
    type: Function,
    default: (fieldName: string) => `[${fieldName}]`
  },
  pageSize: {
    type: Number,
    default: Number.MAX_SAFE_INTEGER
  },
  paginationGap: {
    type: String,
    default: '40px'
  },
  mobileSeparators: {
    type: String as PropType<'between' | 'surrounding' | 'none'>,
    default: 'between'
  },
  mobileRowPadding: {
    type: String,
    default: '20px 0'
  },
  mobileGap: {
    type: String,
    default: '12px 16px'
  },
  overwriteMobile: Boolean,
  searchableCallback: {
    type: Function as PropType<(searchTerm: string, obj: unknown) => boolean>,
    default: null
  },
  filter: {
    type: Object as PropType<BcxTableFilter>,
    default: null
  },
  quickFilter: {
    type: Object as PropType<BcxTableQuickFilter>,
    default: null
  },
  pinnedItems: {
    type: Array as PropType<BcxTablePinnedItemPosition[]>,
    default: () => []
  },
  priorityItems: {
    type: Object as PropType<BcxTablePriorityItems>,
    default: null
  },
  isPaginationInHeader: Boolean,
  progressDisplay: { // Displayed at the bottom right of the table, e.g., 15/30 MB
    type: Object as PropType<BcxTableProgressDisplay>,
    default: null
  }
},
  emits: ['visible-rows', 'search-filter-sort-changed', 'click-table-row-mobile'],
  setup(__props, { emit }) {

const props = __props;




const isSortable = computed(() => !!props.sortableFields.length);

function createTableStateRef<T>(storageSuffix: string, initialValue: T, shouldPersist: boolean): Ref<T> {
  if (!props.persistanceKey || !shouldPersist) return ref(initialValue) as Ref<T>;
  return createStorageBackedRef(`${props.persistanceKey}-${storageSuffix}`, initialValue, props.persistenceMode);
}

const searchTerm = createTableStateRef('tablesearch', '', !!props.searchableCallback);
const filterValue = createTableStateRef<string[]>('tablefilter', [], !!props.filter);
const fieldName = createTableStateRef('tablesort', props.defaultSortField, isSortable.value);
const isAscending = createTableStateRef('tabledir', props.defaultSortAscending, isSortable.value);
const isQuickFilterActive = createTableStateRef('tablequickfilter', props.quickFilter?.isActive, !!props.quickFilter);

const { rows, filter } = toRefs(props);

const page = ref(1);

const { isMobileWidth } = useResponsiveness();
const { locale } = useI18n();

const cssGridTemplateColumns = computed(() => (isMobileWidth.value ? `repeat(${props.headerFields.length}, auto)` : props.templateColumns));

const isMobile = computed(() => props.overwriteMobile || isMobileWidth.value);

const cssGridMobileAreas = computed(() => props.mobileGridAreas
  .split(/\n/)
  .map((row) => `"${row}"`)
  .join('\n'));

const filterItems = computed(() => {
  if (!filter.value) return [];

  return filter.value.states.map((state) => ({
    label: filter.value.labelCallback(state),
    value: state
  }));
});

watchImmediate(filter, (filter) => {
  if (!filter) return;
  if (filterValue.value.length) return;
  if (filter.defaultValue) filterValue.value = [filter.defaultValue];
  else if (filter.isExclusive) filterValue.value = [filter.states[0]];
  else filterValue.value = [...filter.states];
});

const hasContent = computed(() => !!rows.value.length);

const searchFilterSortOptions: BcxTableSearchFilterSortOptions = reactiveComputed(() => ({
  searchCallback: props.searchableCallback,
  sortValueCallback: props.sortValueCallback,
  filter: props.filter,
  quickFilter: props.quickFilter,

  states: filterValue.value,
  sortField: fieldName.value,
  isAscending: isAscending.value,
  searchTerm: searchTerm.value,
  isQuickFilterActive: isQuickFilterActive.value,

  pinnedItems: props.pinnedItems,
  priorityItems: props.priorityItems
}));

async function onQuickFilterClick() {
  if (!props.quickFilter) return;

  const nextStatus = !isQuickFilterActive.value;
  const shouldToggle = await props.quickFilter.beforeToggleCallback?.(nextStatus);

  if (shouldToggle === false) return; // The option to cancel the action

  isQuickFilterActive.value = nextStatus;
}

const isFilterVisible = computed(() => (filter.value?.visibleStates ? filter.value.visibleStates.length : filter.value.states.length));

watch([filterValue, fieldName, isAscending, searchTerm], () => {
  page.value = 1;
  emit('search-filter-sort-changed');
});

const { finalRows } = useBcxTableSearchFilterSort(searchFilterSortOptions, rows);

const displayedHeaderFields = computed(() => {
  if (isMobile.value) {
    return props.sortableFields;
  }
  return props.headerFields;
});

const progressText = computed(() => {
  const { progressDisplay } = props;
  if (!progressDisplay) return '';
  const nf = new Intl.NumberFormat(locale.value);

  return [
    progressDisplay.labelBefore,
    `${nf.format(progressDisplay.currentSize)} ${progressDisplay.prepositionOrSeparators} ${nf.format(progressDisplay.maxSize)}`,
    progressDisplay.unit,
    progressDisplay.labelAfter
  ]
    .filter(Boolean)
    .join(' ');
});

watchImmediate(finalRows, (array) => {
  emit('visible-rows', array);
});

return { __sfc: true,props, emit, isSortable, createTableStateRef, searchTerm, filterValue, fieldName, isAscending, isQuickFilterActive, rows, filter, page, isMobileWidth, locale, cssGridTemplateColumns, isMobile, cssGridMobileAreas, filterItems, hasContent, searchFilterSortOptions, onQuickFilterClick, isFilterVisible, finalRows, displayedHeaderFields, progressText, BCXSortableTableHeader, BCXPaginatedContent, SearchInput, BCXMultiselectFilter, BCXFilterButtons, BCXPagination, BCXButton }
}

})